/**
 * LandingPageCMS - CMS for editing Landing Page content
 * 
 * Allows Super Admin to edit all text content of the public landing page
 * without code changes.
 */

import React, { useState, useEffect } from 'react';
import {
  Globe, Type, Search, MessageCircle, Mail, Layout,
  Save, RefreshCw, Eye, ExternalLink, AlertCircle, CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { api } from '../../App';
import { toast } from 'sonner';

const LandingPageCMS = () => {
  const [content, setContent] = useState({});
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/landing/content');
      setContent(data.flat || {});
      setSections(data.sections || {});
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching landing content:', error);
      toast.error('Erreur lors du chargement du contenu');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/landing/content', { content });
      toast.success('Contenu de la landing page enregistré');
      setHasChanges(false);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const openPreview = () => {
    window.open('/', '_blank');
  };

  // Field definitions organized by section
  const fieldGroups = {
    hero: {
      title: 'Section Hero',
      icon: Layout,
      color: 'violet',
      description: 'La première section visible par les visiteurs',
      fields: [
        { key: 'hero_badge', label: 'Badge / Étiquette', placeholder: 'Logiciel de gestion hôtelière nouvelle génération', help: 'Petit texte au-dessus du titre principal' },
        { key: 'hero_title', label: 'Titre principal', placeholder: 'La plateforme qui transforme...', multiline: true },
        { key: 'hero_subtitle', label: 'Sous-titre', placeholder: 'Créé pour gérer nos établissements...', multiline: true }
      ]
    },
    contact: {
      title: 'Contact',
      icon: MessageCircle,
      color: 'emerald',
      description: 'Informations de contact affichées sur la page',
      fields: [
        { key: 'whatsapp_number', label: 'Numéro WhatsApp', placeholder: '33670133353', help: 'Format international sans le + (ex: 33670133353)' },
        { key: 'contact_email', label: 'Email de contact', placeholder: 'contact@myriadmanager.com', type: 'email' }
      ]
    },
    seo: {
      title: 'SEO',
      icon: Search,
      color: 'blue',
      description: 'Optimisation pour les moteurs de recherche',
      fields: [
        { key: 'meta_title', label: 'Titre SEO (balise title)', placeholder: 'MyRiadManager - Logiciel de Gestion...', help: 'Apparaît dans l\'onglet du navigateur et les résultats Google (50-60 caractères recommandés)' },
        { key: 'meta_description', label: 'Meta Description', placeholder: 'Solution complète de gestion hôtelière...', multiline: true, help: 'Description pour Google (150-160 caractères recommandés)' }
      ]
    },
    sections: {
      title: 'Titres des sections',
      icon: Type,
      color: 'amber',
      description: 'Personnalisez les titres et sous-titres de chaque section',
      fields: [
        { key: 'positioning_title', label: 'Titre - Positionnement', placeholder: 'Une solution pensée pour votre métier' },
        { key: 'positioning_subtitle', label: 'Sous-titre - Positionnement', placeholder: 'Fini les fichiers Excel...', multiline: true },
        { key: 'why_title', label: 'Titre - Pourquoi nous choisir', placeholder: 'Pourquoi choisir MyRiadManager ?' },
        { key: 'why_subtitle', label: 'Sous-titre - Pourquoi nous choisir', placeholder: 'Une plateforme née du terrain...', multiline: true },
        { key: 'features_title', label: 'Titre - Fonctionnalités', placeholder: 'Fonctionnalités complètes' },
        { key: 'features_subtitle', label: 'Sous-titre - Fonctionnalités', placeholder: 'Tous les outils dont vous avez besoin...' },
        { key: 'journey_title', label: 'Titre - Parcours client', placeholder: 'Un parcours client fluide et maîtrisé' },
        { key: 'journey_subtitle', label: 'Sous-titre - Parcours client', placeholder: 'De la réservation au check-out...' },
        { key: 'pricing_title', label: 'Titre - Tarifs', placeholder: 'Tarifs transparents' },
        { key: 'pricing_subtitle', label: 'Sous-titre - Tarifs', placeholder: 'Choisissez la formule adaptée...' },
        { key: 'contact_title', label: 'Titre - Contact', placeholder: 'Discutons de votre établissement' },
        { key: 'contact_subtitle', label: 'Sous-titre - Contact', placeholder: 'Une question ? Un projet ?...' }
      ]
    },
    footer: {
      title: 'Footer',
      icon: Globe,
      color: 'slate',
      description: 'Pied de page',
      fields: [
        { key: 'footer_copyright', label: 'Copyright', placeholder: 'MyRiadManager. Tous droits réservés.' }
      ]
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      violet: { bg: 'bg-violet-50', border: 'border-violet-200', headerBg: 'bg-violet-50', icon: 'bg-violet-100 border-violet-200 text-violet-600', btn: 'bg-violet-600 hover:bg-violet-700' },
      emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', headerBg: 'bg-emerald-50', icon: 'bg-emerald-100 border-emerald-200 text-emerald-600', btn: 'bg-emerald-600 hover:bg-emerald-700' },
      blue: { bg: 'bg-blue-50', border: 'border-blue-200', headerBg: 'bg-blue-50', icon: 'bg-blue-100 border-blue-200 text-blue-600', btn: 'bg-blue-600 hover:bg-blue-700' },
      amber: { bg: 'bg-amber-50', border: 'border-amber-200', headerBg: 'bg-amber-50', icon: 'bg-amber-100 border-amber-200 text-amber-600', btn: 'bg-amber-600 hover:bg-amber-700' },
      slate: { bg: 'bg-slate-50', border: 'border-slate-200', headerBg: 'bg-slate-50', icon: 'bg-slate-100 border-slate-200 text-slate-600', btn: 'bg-slate-600 hover:bg-slate-700' }
    };
    return colors[color] || colors.slate;
  };

  const renderFieldGroup = (groupKey) => {
    const group = fieldGroups[groupKey];
    if (!group) return null;
    
    const colors = getColorClasses(group.color);
    const Icon = group.icon;
    
    return (
      <Card className={`border-2 ${colors.border}`}>
        <CardHeader className={`${colors.headerBg} border-b ${colors.border}`}>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl ${colors.icon} border flex items-center justify-center`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <CardTitle className="text-slate-800">{group.title}</CardTitle>
              <CardDescription className="text-slate-500 mt-1">
                {group.description}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-6">
            {group.fields.map((field) => (
              <div key={field.key}>
                <Label 
                  htmlFor={field.key}
                  className="text-sm font-medium text-slate-700 flex items-center gap-2"
                >
                  {field.label}
                </Label>
                {field.help && (
                  <p className="text-xs text-slate-400 mt-0.5 mb-1">{field.help}</p>
                )}
                {field.multiline ? (
                  <Textarea
                    id={field.key}
                    data-testid={`landing-${field.key}`}
                    value={content[field.key] || ''}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    className="mt-1.5 min-h-[80px] resize-none"
                  />
                ) : (
                  <Input
                    id={field.key}
                    data-testid={`landing-${field.key}`}
                    type={field.type || 'text'}
                    value={content[field.key] || ''}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    className="mt-1.5"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-6">
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className={colors.btn}
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landing-page-cms">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Landing Page</h2>
          <p className="text-slate-500 mt-1">
            Éditez le contenu de la page vitrine publique
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <AlertCircle className="w-3 h-3 mr-1" />
              Modifications non sauvegardées
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={openPreview}
            className="gap-2"
          >
            <Eye className="w-4 h-4" />
            Prévisualiser
            <ExternalLink className="w-3 h-3" />
          </Button>
          <Button
            variant="outline"
            onClick={fetchContent}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Preview Card */}
      <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-0">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg mb-1">Page vitrine MyRiadManager</h3>
              <p className="text-emerald-100 text-sm">
                Cette page est accessible publiquement à l'adresse racine de votre site. 
                Les modifications sont appliquées immédiatement.
              </p>
            </div>
            <Button 
              onClick={openPreview}
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Ouvrir la page
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
          <TabsTrigger value="hero" className="gap-2" data-testid="tab-hero">
            <Layout className="w-4 h-4" />
            <span className="hidden sm:inline">Hero</span>
          </TabsTrigger>
          <TabsTrigger value="contact" className="gap-2" data-testid="tab-contact">
            <MessageCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Contact</span>
          </TabsTrigger>
          <TabsTrigger value="seo" className="gap-2" data-testid="tab-seo">
            <Search className="w-4 h-4" />
            <span className="hidden sm:inline">SEO</span>
          </TabsTrigger>
          <TabsTrigger value="sections" className="gap-2" data-testid="tab-sections">
            <Type className="w-4 h-4" />
            <span className="hidden sm:inline">Sections</span>
          </TabsTrigger>
          <TabsTrigger value="footer" className="gap-2" data-testid="tab-footer">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Footer</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hero">
          {renderFieldGroup('hero')}
        </TabsContent>

        <TabsContent value="contact">
          {renderFieldGroup('contact')}
        </TabsContent>

        <TabsContent value="seo">
          {renderFieldGroup('seo')}
          
          {/* SEO Preview */}
          <Card className="mt-6 bg-slate-50 border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-slate-700 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Aperçu Google
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-lg border border-slate-200 p-4 max-w-xl">
                <div className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
                  {content.meta_title || 'MyRiadManager - Logiciel de Gestion pour Riads'}
                </div>
                <div className="text-green-700 text-sm mt-1">
                  https://myriadmanager.com
                </div>
                <div className="text-slate-600 text-sm mt-1 line-clamp-2">
                  {content.meta_description || 'Solution complète de gestion hôtelière pour riads, maisons d\'hôtes et conciergeries.'}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sections">
          {renderFieldGroup('sections')}
        </TabsContent>

        <TabsContent value="footer">
          {renderFieldGroup('footer')}
        </TabsContent>
      </Tabs>

      {/* Info box about plans */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900">Plans tarifaires dynamiques</h4>
              <p className="text-sm text-blue-700 mt-1">
                Les tarifs affichés sur la landing page sont automatiquement synchronisés avec vos plans définis dans 
                <strong> Abonnements &gt; Plans</strong>. Seuls les plans actifs et publics sont affichés.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LandingPageCMS;
